import { useMutation, useQuery } from "@tanstack/react-query";
import { RotateCw } from "lucide-react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@lawline/ui/components/dialog";
import { Skeleton } from "@lawline/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";

import { useAuth } from "@platform/auth/context";
import { formatDateTime } from "@platform/fmt";
import { redeliverMutation } from "../webhooks.mutations";
import { webhookDeliveryQueryOptions } from "../webhooks.queries";
import type { WebhookEndpoint } from "../webhooks.types";
import { lastResponse } from "../webhooks.utils";
import { WebhookDeliveryStatusBadge } from "./webhook-delivery-status-badge";
import { WebhookErrorText } from "./webhook-error-text";

// The excerpt is what the sender stored of the receiver's answer, at most 1 KiB. Nothing more of
// it is kept, so nothing more can be shown.
export function WebhookDeliveryDetail({
	endpoint,
	deliveryId,
	onClose,
}: {
	endpoint: WebhookEndpoint;
	deliveryId: string;
	onClose: () => void;
}) {
	const t = useTranslations("webhooks.delivery");
	const { can } = useAuth();
	const delivery = useQuery(webhookDeliveryQueryOptions(endpoint.id, deliveryId));
	const redeliver = useMutation(redeliverMutation);
	const row = delivery.data;

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{t("title")}</DialogTitle>
					<DialogDescription className="font-mono text-xs break-all">
						{deliveryId}
					</DialogDescription>
				</DialogHeader>

				{delivery.isPending ? (
					<Skeleton className="h-32 w-full" />
				) : !row ? (
					<p role="alert" className="text-destructive text-sm">
						{t("error")}
					</p>
				) : (
					<div className="flex min-w-0 flex-col gap-4">
						<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
							<dt className="text-muted-foreground">{t("eventTypeLabel")}</dt>
							<dd className="font-mono text-xs">{row.eventType}</dd>
							<dt className="text-muted-foreground">{t("eventIdLabel")}</dt>
							<dd className="font-mono text-xs break-all">{row.eventId}</dd>
							{row.runId && (
								<>
									<dt className="text-muted-foreground">{t("runLabel")}</dt>
									<dd className="font-mono text-xs break-all">{row.runId}</dd>
								</>
							)}
						</dl>

						<div className="flex flex-wrap items-center gap-2">
							<WebhookDeliveryStatusBadge status={row.status} />
							{can({ webhook: ["update"] }) && endpoint.status === "enabled" && (
								<Button
									size="sm"
									variant="outline"
									disabled={redeliver.isPending}
									onClick={() => redeliver.mutate({ endpointId: endpoint.id, deliveryId })}
								>
									<RotateCw className="size-4" />
									{t("redeliver")}
								</Button>
							)}
						</div>
						<p className="text-muted-foreground text-xs">{t("redeliverHint")}</p>
						{redeliver.isSuccess && (
							<p role="status" className="text-muted-foreground text-sm">
								{t("redeliverQueued")}
							</p>
						)}
						{redeliver.error && <WebhookErrorText error={redeliver.error} />}

						<div className="flex flex-col gap-2">
							<h3 className="text-sm font-medium">{t("attemptsLabel")}</h3>
							{row.attempts.length === 0 ? (
								<p className="text-muted-foreground text-sm">{t("noAttempts")}</p>
							) : (
								<div className="overflow-x-auto rounded-md border">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>{t("columns.attempt")}</TableHead>
												<TableHead>{t("columns.trigger")}</TableHead>
												<TableHead>{t("columns.started")}</TableHead>
												<TableHead>{t("columns.duration")}</TableHead>
												<TableHead>{t("columns.response")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{row.attempts.map((attempt) => (
												<TableRow key={attempt.attempt} className="align-top">
													<TableCell>{attempt.attempt}</TableCell>
													<TableCell>{t(`trigger.${attempt.trigger}`)}</TableCell>
													<TableCell className="whitespace-nowrap">
														{formatDateTime(attempt.startedAt)}
													</TableCell>
													<TableCell className="whitespace-nowrap">
														{t("durationValue", { ms: attempt.durationMs })}
													</TableCell>
													<TableCell className="min-w-40">
														<span className="font-mono text-xs">
															{lastResponse({
																lastStatusCode: attempt.statusCode,
																lastErrorCode: attempt.errorCode,
															})}
														</span>
														{attempt.responseExcerpt && (
															<pre
																aria-label={t("excerptLabel")}
																className="bg-muted mt-1 max-h-32 overflow-auto rounded p-2 text-xs whitespace-pre-wrap"
															>
																{attempt.responseExcerpt}
															</pre>
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<h3 className="text-sm font-medium">{t("payloadLabel")}</h3>
							<pre className="bg-muted overflow-auto rounded-md p-3 text-xs">
								{JSON.stringify(row.payload, null, 2)}
							</pre>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
